"use client";

import Image from '@/components/ui/image';
import { CircleAlert, Hash, RefreshCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

import { popularityDots } from "@/lib/keyword-explorer";
import { rankTier } from "@/lib/keyword-rank-tiers";
import { difficultyTier } from "@/lib/keywords/keyword-scores";
import { cn } from "@/lib/utils";

/**
 * The cells every keyword surface draws, in one file so the five of them cannot
 * disagree. The landing grid, the detail page, the compare view and the app
 * page's two keyword blocks all print difficulty, Apple popularity, an app icon,
 * a stack of icons and a 14-day line.
 *
 * The rank pill IS here, as `RankPill`. It was left out on the grounds that
 * `rankTier` already owns the bands, and four surfaces then wrote the same two
 * class lines and the same terminal ternary around it. The bands still live in
 * `rankTier` — this only renders them. The up/down change is still NOT here:
 * `KeywordMovement` is already one component with one home.
 *
 * The bands and the dot arithmetic stay out of this file for the same reason:
 * `difficultyTier` owns the 25 / 50 / 75 bounds and `popularityDots` owns the dot
 * mapping, both pinnable in the node environment this repository's vitest runs
 * in, which a component is not.
 */

/**
 * The bar fill per band. `difficultyTier` decides which band a score is in; this
 * is only the one solid colour a 6 px fill needs, because `tier.chip` is a
 * tinted chip background (`bg-rose-500/10`) that disappears at this size.
 * Never add a bound here — the bounds are `difficultyTier`'s.
 */
const DIFFICULTY_FILL: Record<string, string> = {
  Easy: "bg-emerald-600 dark:bg-emerald-500",
  Moderate: "bg-amber-500 dark:bg-amber-400",
  Hard: "bg-rose-500 dark:bg-rose-400",
  "Very hard": "bg-rose-700 dark:bg-rose-500",
};

export function DifficultyBar({ score, title }: { score: number | null; title?: string }) {
  if (score === null) {
    return (
      <span
        className="text-muted-foreground"
        title={title ?? "Not scored yet on any sweep we hold"}
      >
        n/a
      </span>
    );
  }

  const tier = difficultyTier(score);
  const fill = Math.max(0, Math.min(100, score));
  return (
    <span
      className="inline-flex items-center gap-2 font-mono text-[13px] font-semibold tabular-nums"
      title={
        title ??
        `Shortimize difficulty ${score}/100 (${tier.label.toLowerCase()}) — our estimate, not Apple's.`
      }
    >
      <span>{score}</span>
      <span className="block h-1.5 w-10 overflow-hidden rounded-full bg-muted">
        <span
          className={cn("block h-full rounded-full", DIFFICULTY_FILL[tier.label])}
          style={{ width: `${fill}%` }}
        />
      </span>
    </span>
  );
}

/**
 * Apple's popularity for one keyword, as dots.
 *
 * `kw_hints.rank_at_term` is a PLACE inside Apple's ten suggestions, not a
 * magnitude, so a bar length would claim a size the number does not carry.
 */
export function PopularityDots({
  rankAtTerm,
  hintsAtTerm,
  title,
}: {
  rankAtTerm: number | null;
  hintsAtTerm: number | null;
  title?: string;
}) {
  if (rankAtTerm === null) {
    return (
      <span
        className="font-mono text-muted-foreground"
        title={title ?? "Not among Apple's ten suggestions"}
      >
        –
      </span>
    );
  }

  const lit = popularityDots(rankAtTerm);
  return (
    <span
      className="inline-flex items-center gap-[3px]"
      title={title ?? `#${rankAtTerm} of ${hintsAtTerm ?? 10} Apple suggestions`}
    >
      {[0, 1, 2, 3, 4].map((index) => (
        <span
          key={index}
          className={cn(
            "block h-[7px] w-[7px] rounded-full",
            index < lit ? "bg-foreground" : "bg-border",
          )}
        />
      ))}
    </span>
  );
}

const ICON_PX = { sm: 20, md: 32, lg: 48 } as const;

/**
 * One app icon. The app's name is always printed beside it, so `alt` is empty
 * and the icon is decorative; a missing `icon_url` draws a muted square of the
 * same box rather than a broken image, because `app_metadata` is not backfilled
 * and an absent row is normal.
 */
export function AppIcon({
  src,
  name,
  size = "md",
}: {
  src: string | null | undefined;
  name?: string | null;
  size?: "sm" | "md" | "lg";
}) {
  const px = ICON_PX[size];
  const box = "flex-none rounded-md border border-border bg-muted object-cover";

  if (!src) {
    return (
      <span
        aria-hidden="true"
        className={cn("block", box)}
        style={{ width: px, height: px }}
        title={name ?? undefined}
      />
    );
  }

  return (
    <Image
      src={src}
      alt=""
      width={px}
      height={px}
      sizes={`${px}px`}
      className={box}
      title={name ?? undefined}
    />
  );
}

export function AppIconStack({
  apps,
  max = 3,
}: {
  apps: { app_store_id: number; name: string | null; icon_url: string | null }[];
  max?: number;
}) {
  const shown = apps.slice(0, Math.max(0, max));
  const rest = apps.length - shown.length;
  return (
    <span className="inline-flex items-center">
      {shown.map((app) => (
        <span
          key={app.app_store_id}
          className="-ml-1.5 inline-flex rounded-md ring-2 ring-background first:ml-0"
        >
          <AppIcon src={app.icon_url} name={app.name} size="sm" />
        </span>
      ))}
      {rest > 0 ? <span className="ml-1 text-[12px] text-muted-foreground">+{rest}</span> : null}
    </span>
  );
}

/**
 * A keyword's recent positions as one line. A smaller rank is a better result,
 * so the smallest value is drawn at the TOP of the box: a climb reads as a rise.
 *
 * Under two readable points it draws an empty svg of the same box instead of
 * nothing, so a table row does not change height when one keyword has a single
 * sweep and its neighbour has fourteen.
 */
export function Sparkline({
  points,
  width = 80,
  height = 20,
  className,
}: {
  points: (number | null)[];
  width?: number;
  height?: number;
  className?: string;
}) {
  const values = points.filter((point): point is number => point !== null);
  if (values.length < 2) {
    return <svg width={width} height={height} className={className} aria-hidden="true" />;
  }

  const low = Math.min(...values);
  const span = Math.max(...values) - low || 1;
  const line = values
    .map((value, index) => {
      const x = (index / (values.length - 1)) * (width - 2) + 1;
      const y = ((value - low) / span) * (height - 4) + 2;
      return `${x},${y}`;
    })
    .join(" ");

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={className}
      aria-hidden="true"
    >
      <polyline
        points={line}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * An App Store position as the chip every keyword surface draws it.
 *
 * `min-w` holds #5 and #188 to one chip width so a column of them reads as a
 * single edge rather than as ragged pills. A null rank keeps the chip and its
 * outline and prints an em dash: the row still has a cell, and the app simply
 * holds no position there.
 *
 * The bands are `rankTier`'s and nothing here second-guesses them. `terminal`
 * is the app page's theme, where the same five steps are an emerald ramp.
 */
export function RankPill({
  rank,
  terminal,
  title,
  className,
}: {
  rank: number | null;
  terminal?: boolean;
  title?: string;
  className?: string;
}) {
  const tier = rank === null ? null : rankTier(rank);
  return (
    <span
      className={cn(
        "inline-flex min-w-[3.25rem] items-center justify-center rounded-full px-2 py-0.5",
        "text-[12px] font-semibold leading-[1.4] tabular-nums",
        tier === null
          ? "text-muted-foreground ring-1 ring-border/70"
          : terminal
            ? cn("font-mono", tier.terminalChip)
            : tier.chip,
        className,
      )}
      title={title}
    >
      {rank === null ? "\u2014" : `#${rank}`}
    </span>
  );
}

/**
 * What a difficulty score is, in one sentence, for the keyword landing surfaces.
 * The app detail page carries the popularity half only: the owner read the
 * difficulty explainer off the app page and asked for it gone.
 */
export const DIFFICULTY_FOOTNOTE =
  "Difficulty is our 0 to 100 estimate over the keywords swept that day: " +
  "easy under 25, moderate to 49, hard to 74, very hard from 75.";

export const POPULARITY_FOOTNOTE =
  "Popularity is the keyword's place in Apple's ten search suggestions; a dash means Apple does not suggest it.";

export const DIFFICULTY_POPULARITY_FOOTNOTE = `${DIFFICULTY_FOOTNOTE} ${POPULARITY_FOOTNOTE}`;

export const MOVED_FOOTNOTE =
  "Moved counts the places apps changed inside the top 25 since the last sweep, added up over " +
  "the keyword.";

/**
 * The three states every keyword surface draws when it has no rows to draw.
 *
 * One look each, deliberately. Five copies of the same alert is five chances to
 * word the same failure differently, and the mock ships exactly one `errorBox`,
 * one `emptyBox` and one `skelRows` for all five screens. `className` carries a
 * caller's spacing and, on the app panel, its terminal-theme font — never a
 * second look.
 *
 * The box is a `ring-border` ring rather than a destructive tint: `--border` is
 * already forest green under `.terminal` and neutral under light and dark, so
 * the one rule reads correctly in all three and nested inside a Card, which a
 * red tint on a green panel does not.
 */
export function KeywordErrorNote({
  what,
  onRetry,
  className,
}: {
  what: string;
  onRetry?: () => void;
  className?: string;
}) {
  return (
    <div
      role="alert"
      className={cn("flex items-start gap-2 rounded-md p-3 ring-1 ring-border", className)}
    >
      <CircleAlert className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <div className="text-[12px] leading-snug">
        <p className="font-medium">Could not load {what}</p>
        <p className="m-0 text-muted-foreground">
          The keyword service did not answer. Your tracked keywords are safe; try again in a moment.
        </p>
        {/* No `onRetry` means no button, and the two trending cards and the
            Search positions card pass none on purpose: they own no write, the
            query refetches itself on the next mount, and a button that re-runs a
            read the user cannot influence invites the same failure again. */}
        {onRetry ? (
          <Button variant="outline" size="sm" className="mt-3" onClick={onRetry}>
            <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
            Retry
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export function KeywordEmptyNote({
  title,
  body,
  className,
}: {
  title: string;
  body: string;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col items-center gap-1 px-6 py-10 text-center", className)}>
      <Hash className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
      <p className="text-[13px] font-medium">{title}</p>
      <p className="m-0 max-w-md text-[12px] text-muted-foreground">{body}</p>
    </div>
  );
}

/**
 * Placeholder rows while a keyword read is in flight. `aria-hidden`, because a
 * screen reader gains nothing from the count of boxes we guessed at.
 */
export function KeywordSkeletonRows({
  rows,
  height = "h-10",
  className,
}: {
  rows: number;
  height?: string;
  className?: string;
}) {
  return (
    <div className={cn("space-y-2", className)} aria-hidden="true">
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton key={index} className={cn("w-full", height)} />
      ))}
    </div>
  );
}
